import { apiClient } from '../client';
import { Step, StepMetadata } from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('StepsService');

export class StepsService {
  /**
   * Fetch all transform steps
   */
  async getTransforms(): Promise<Step[]> {
    try {
      logger.debug('Fetching transforms...');
      const transforms = await apiClient.get<Step[]>('/v1/transforms');
      
      // Add stepType to each transform
      return transforms.map(transform => ({
        ...transform,
        stepType: 'transform' as const,
      }));
    } catch (error) {
      logger.error('Failed to fetch transforms:', error);
      throw error;
    }
  }

  /**
   * Fetch all action steps
   */
  async getActions(): Promise<Step[]> {
    try {
      logger.debug('Fetching actions...');
      const actions = await apiClient.get<Step[]>('/v1/actions');
      
      // Add stepType to each action
      return actions.map(action => ({
        ...action,
        stepType: 'action' as const,
      }));
    } catch (error) {
      logger.error('Failed to fetch actions:', error);
      throw error;
    }
  }

  /**
   * Fetch all steps (transforms and actions combined)
   */
  async getAllSteps(): Promise<Step[]> {
    try {
      logger.debug('Fetching all steps...');
      
      // Fetch both in parallel
      const [transforms, actions] = await Promise.all([
        this.getTransforms(),
        this.getActions(),
      ]);

      const allSteps = [...transforms, ...actions];
      
      logger.debug('Steps fetched successfully:', {
        transformsCount: transforms.length,
        actionsCount: actions.length,
        totalCount: allSteps.length,
      });

      return allSteps;
    } catch (error) {
      logger.error('Failed to fetch all steps:', error);
      throw error;
    }
  }

  /**
   * Get step by type
   */
  getStepByType(steps: Step[], type: string): Step | undefined {
    const found = steps.find(step => step.type === type);
    return found;
  }

  /**
   * Get step icon by type
   */
  getStepIcon(steps: Step[], type: string): string | null {
    const step = this.getStepByType(steps, type);
    return step?.svg || null;
  }

  /**
   * Get step metadata by type
   */
  getStepMetadata(steps: Step[], type: string): StepMetadata | null {
    const step = this.getStepByType(steps, type);
    return step ? {
      name: step.name,
      category: step.category,
      type: step.type,
      nodeType: step.nodeType,
      svg: step.svg,
      keywords: step.keywords,
    } : null;
  }

  /**
   * Get step by node type
   */
  getStepByNodeType(steps: Step[], nodeType: string): Step | undefined {
    const found = steps.find(step => step.nodeType === nodeType);
    logger.debug('getStepByNodeType:', nodeType, found ? `found: ${found.name}` : 'not found');
    return found;
  }

  /**
   * Get step icon by node type
   */
  getStepIconByNodeType(steps: Step[], nodeType: string): string | null {
    const step = this.getStepByNodeType(steps, nodeType);
    return step?.svg || null;
  }

  /**
   * Search steps by keyword
   */
  searchSteps(steps: Step[], query: string): Step[] {
    const lowerQuery = query.toLowerCase();
    return steps.filter(step => 
      step.name.toLowerCase().includes(lowerQuery) ||
      step.category.toLowerCase().includes(lowerQuery) ||
      step.keywords.some(keyword => keyword.toLowerCase().includes(lowerQuery))
    );
  }

  /**
   * Get steps by category
   */
  getStepsByCategory(steps: Step[], category: string): Step[] {
    return steps.filter(step => step.category === category);
  }

  /**
   * Get unique categories
   */
  getCategories(steps: Step[]): string[] {
    const categories = new Set(steps.map(step => step.category));
    return Array.from(categories).sort();
  }

  /**
   * Get steps by step type (transform or action)
   */
  getStepsByStepType(steps: Step[], stepType: 'transform' | 'action'): Step[] {
    return steps.filter(step => step.stepType === stepType);
  }
}

// Export singleton instance
export const stepsService = new StepsService(); 